import React, { FunctionComponent, useContext, useMemo } from "react";
import { Theme, makeStyles, CardProps, Grid, Typography, Fade, useTheme } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { LoginSectionButton } from "./form-components/login-section-button";
import { LoginFormSection } from "./login-form-section";
import { LOGIN_FORM_TYPE } from "../../models/login-form-type";
import { LoginContext } from "./context/login-context";
import { getLocalizedLinks } from "../../translations/links";
import { BeatLoader } from "react-spinners";
import { CardWithArrow } from "../../components/card-with-arrow/card-with-arrow";

export const LOGIN_SECTION_HEIGHT = 245;
export const MOBILE_REGISTER_SECTION_HEIGHT = 440;

interface ILoginCardStyleProps {
  formType: LOGIN_FORM_TYPE;
}

const useStyles = makeStyles((theme: Theme) => ({
  section: {
    width: "70%",
    [theme.breakpoints.down("sm")]: {
      width: "95%",
    },
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
  },
  loginSection: {
    minHeight: LOGIN_SECTION_HEIGHT,
    transition: "min-height 0.50s ease-out",
    [theme.breakpoints.down("xs")]: {
      minHeight: ({ formType }: ILoginCardStyleProps) =>
        formType === LOGIN_FORM_TYPE.REGISTER ? MOBILE_REGISTER_SECTION_HEIGHT : LOGIN_SECTION_HEIGHT,
    },
    justifyContent: "center",
  },
  lastSection: {
    marginTop: theme.spacing(10),
    marginBottom: theme.spacing(5),
  },
  firstSection: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(6),
  },
  link: {
    textDecoration: "underline",
    fontWeight: 300,
    cursor: "pointer",
    fontSize: 16,
    marginBottom: theme.spacing(2),
  },
  agbLink: {
    textDecoration: "underline",
    color: theme.palette.grey[500],
    fontSize: 12,
    fontWeight: 300,
    cursor: "pointer",
  },
  resendCodeContainer: {
    height: 30,
  },
}));

interface ILoginCardProps extends CardProps {}

export const LoginCard: FunctionComponent<ILoginCardProps> = (props) => {
  const { className } = props;
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { formType, setFormType, onLogin, email, password, loginLoading } = useContext(LoginContext);

  const classes = useStyles({ formType });

  const privacyPolicyLink = useMemo(() => {
    const { PRIVACY_POLICY } = getLocalizedLinks(i18n);
    return PRIVACY_POLICY;
  }, [i18n]);

  return (
    <CardWithArrow showArrow={false} className={className}>
      <Grid container direction="column" alignItems="center">
        <Grid item className={`${classes.section} ${classes.firstSection}`}>
          <Grid container>
            <Grid item xs={6}>
              <LoginSectionButton
                selected={formType === LOGIN_FORM_TYPE.LOGIN || formType === LOGIN_FORM_TYPE.CODE}
                onClick={() => setFormType(LOGIN_FORM_TYPE.LOGIN)}
              >
                {t("login.sign_in")}
              </LoginSectionButton>
            </Grid>
            <Grid item xs={6}>
              <LoginSectionButton
                selected={formType === LOGIN_FORM_TYPE.REGISTER}
                onClick={() => setFormType(LOGIN_FORM_TYPE.REGISTER)}
              >
                {t("login.sign_up")}
              </LoginSectionButton>
            </Grid>
          </Grid>
        </Grid>
        <Grid item className={`${classes.section} ${classes.loginSection}`}>
          <LoginFormSection />
        </Grid>
        <Grid item className={`${classes.section} ${classes.lastSection}`}>
          <Grid container justify="center" direction="column" alignItems="center">
            <Fade in={formType === LOGIN_FORM_TYPE.CODE && !!email && !!password}>
              <div className={classes.resendCodeContainer}>
                {loginLoading && <BeatLoader size={12} color={theme.palette.primary.main} />}
                {!loginLoading && (
                  <Typography color="primary" align="center" className={classes.link} onClick={onLogin}>
                    {t("login.resend_code")}
                  </Typography>
                )}
              </div>
            </Fade>
            <Typography
              color="primary"
              align="center"
              className={classes.link}
              onClick={() => setFormType(LOGIN_FORM_TYPE.PASSWORD_FORGOTTEN)}
            >
              {t("login.forgot_password")}
            </Typography>
            <a href={privacyPolicyLink}>
              <Typography align="center" className={classes.agbLink}>
                {t("general.privacy_policy")}
              </Typography>
            </a>
          </Grid>
        </Grid>
      </Grid>
    </CardWithArrow>
  );
};
